// src/access.ts
import { useAuthStore } from '@/stores/authStore';
import type { API } from '@/services/ant-design-pro/typings';
import {
  hasPermission,
  hasAnyPermission,
  hasGranularPermission,
  canView,
  canEdit,
  canAccessRoute,
  canViewPageResource,
  isSuperadminRole,
} from '@/utils/permissions';

export default (initialState: { currentUser?: API.CurrentUser }) => {
  const { currentUser } = initialState ?? {};
  const { user } = useAuthStore.getState();

  // Prefer Zustand store user, fallback to initialState
  const effectiveUser = user 
    ? {
        role: user.role,
        access: isSuperadminRole(user.role) ? 'admin' : user.role,
        permissions: user.permissions,
      }
    : currentUser;

  // Get user permissions (backend permission codes)
  // Always get fresh permissions from store to ensure we have latest data
  const storeUser = useAuthStore.getState().user;
  const userPermissions = (storeUser?.permissions || effectiveUser?.permissions || []);

  // Only superadmin bypasses permission checks — role "admin" uses assigned permissions
  const isSuperAdmin = isSuperadminRole(effectiveUser?.role);
  
  const isPlatformAdmin =
    isSuperAdmin ||
    String(effectiveUser?.access ?? '').trim().toUpperCase() === 'PLATFORM_ADMIN';
  const isOperator = ['OPERATOR', 'CS', 'LOGISTICS', 'FINANCE'].includes(
    effectiveUser?.access || '',
  );

  // Permission-based access control
  // Superadmins have full access to everything, others need specific permissions
  const hasOrderView =
    hasGranularPermission(userPermissions, 'order-statistics:view') ||
    userPermissions.some((p) => p.startsWith('order-status:') && p.endsWith(':view')) ||
    canView(userPermissions, 'orders');

  const hasOrderEdit =
    userPermissions.some(
      (p) => p.startsWith('order-status:') && !p.endsWith(':view'),
    ) || hasPermission(userPermissions, 'orders:write') || hasPermission(userPermissions, 'user-orders:write');

  // Granular page resources per menu group, mirroring the backend permission matrix
  // (backend/src/shared/constants/permission-matrix.constants.ts). The backend stores
  // granular `${resource}:view` / `${resource}:edit` codes (e.g. `inspection-list:view`),
  // NOT the legacy category codes (`warehouses:read`, `products:read`, ...) that the older
  // `canView()`/`hasPermission()` checks expect. `canViewPageResource()` checks
  // `${resource}:view` directly, so it recognizes the codes the matrix actually issues.
  const PRODUCT_PAGE_RESOURCES = [
    'company-mall', 'live-products', 'live-sellers', 'category-management',
    // 'live-page-settings' 는 'frontend-pages' 로 바뀐 옛 코드지만, 그 코드만 가진
    // 관리자가 운영관리 그룹 헤더를 통째로 잃지 않도록 목록에 남겨 둔다.
    'seller-teams', 'simplified-product-register', 'frontend-pages', 'live-page-settings',
  ];
  const STOREHOUSE_PAGE_RESOURCES = ['inspection-list', 'rack-management', 'inventory-management', 'warehouse-address'];
  const LOGISTICS_PAGE_RESOURCES = [
    'waybill-informations', 'waybill-bandwidth', 'transportation-methods',
    'regional-management', 'collect-orders', 'goods-management',
    'international-tracking-number', 'hs-product-code',
  ];
  const MEMBER_PAGE_RESOURCES = [
    'member-statistics', 'active-users', 'general-members', 'promotion-detail', 'member-documents',
  ];
  // Operation pages that the routes gate behind canManageSettings
  const OPERATION_SETTINGS_RESOURCES = ['coupon-point-settings', 'rebate-settings', 'search-management'];
  // System → Settings pages
  const SYSTEM_SETTINGS_RESOURCES = [
    'exchange-tariff-setting', 'add-on-services', 'island-mountain-cost', 'worker-log',
    'product-price-increase', 'service-fee-setting', 'purchase-condition-setting', 'additional-service-price',
  ];
  const CS_PAGE_RESOURCES = [
    'inquiry', 'notices', 'site-evaluation', 'review', 'help-center', 'banner-event', 'suggestion',
  ];

  const canViewAnyPage = (resources: string[]) =>
    resources.some((r) => canViewPageResource(userPermissions, r));
  const canEditAnyPage = (resources: string[]) =>
    resources.some((r) => hasGranularPermission(userPermissions, `${r}:edit`));

  // 공유 리베이트 — 설정(운영 관리)과 모니터링(정산 관리). 새 코드라 옛 별칭이 없고,
  // 설정 행은 OPERATION_SETTINGS_RESOURCES 에 넣지 않는다(그 목록은 일반 설정 권한
  // canViewSettings/canManageSettings 까지 바꾼다) — 운영관리 그룹 헤더에만 직접 더한다.
  const shareRebateSettingsView = canViewPageResource(userPermissions, 'share-rebate-settings');
  const shareRebatesView = canViewPageResource(userPermissions, 'share-rebates');

  // Granular-aware view/edit aggregates for each menu group (legacy category fallback kept)
  const operationDataView = canViewPageResource(userPermissions, 'operation-data');
  const operationSettingsView = canViewAnyPage(OPERATION_SETTINGS_RESOURCES);
  const productsView = canView(userPermissions, 'products') || canViewAnyPage(PRODUCT_PAGE_RESOURCES);
  const productsEdit = canEdit(userPermissions, 'products') || canEditAnyPage(PRODUCT_PAGE_RESOURCES);
  const viewOperationPage = (resource: string) => {
    if (canViewPageResource(userPermissions, resource)) return true;
    if (resource === 'category-management') return canView(userPermissions, 'categories');
    if (
      resource === 'company-mall' ||
      resource === 'live-products' ||
      resource === 'live-sellers' ||
      resource === 'seller-teams'
    ) {
      return canView(userPermissions, 'products');
    }
    return false;
  };
  const viewOperationSettingsPage = (resource: string) =>
    canViewPageResource(userPermissions, resource) ||
    hasPermission(userPermissions, 'settings:read');
  const storehouseOnlyView =
    canView(userPermissions, 'warehouses') ||
    canView(userPermissions, 'racks') ||
    canViewAnyPage(STOREHOUSE_PAGE_RESOURCES);
  const logisticsView = canView(userPermissions, 'packages') || canViewAnyPage(LOGISTICS_PAGE_RESOURCES);
  const logisticsEdit = canEdit(userPermissions, 'packages') || canEditAnyPage(LOGISTICS_PAGE_RESOURCES);
  // Per-page view helpers — granular matrix codes first, legacy category fallback for older roles.
  const viewStorehousePage = (resource: string) =>
    canViewPageResource(userPermissions, resource) || canView(userPermissions, 'warehouses');
  const viewLogisticsPage = (resource: string) =>
    canViewPageResource(userPermissions, resource) || canView(userPermissions, 'packages');
  // The /storehouse menu group hosts both warehouse and logistics pages, so the group is
  // visible if the admin can view any of them.
  const storehouseGroupView = storehouseOnlyView || logisticsView;
  const membersView =
    canView(userPermissions, 'users') ||
    canEdit(userPermissions, 'users') ||
    hasPermission(userPermissions, 'users:delete') ||
    canViewAnyPage(MEMBER_PAGE_RESOURCES);
  const settingsView =
    hasPermission(userPermissions, 'settings:read') ||
    hasPermission(userPermissions, 'settings:write') ||
    canViewAnyPage(OPERATION_SETTINGS_RESOURCES) ||
    canViewAnyPage(SYSTEM_SETTINGS_RESOURCES);
  const settingsEdit =
    hasPermission(userPermissions, 'settings:write') ||
    canEditAnyPage(OPERATION_SETTINGS_RESOURCES) ||
    canEditAnyPage(SYSTEM_SETTINGS_RESOURCES);
  const systemSettingsGroupView =
    canViewPageResource(userPermissions, 'administrators') ||
    canViewAnyPage(SYSTEM_SETTINGS_RESOURCES) ||
    hasPermission(userPermissions, 'settings:read');
  const viewSystemSettingsPage = (resource: string) =>
    canViewPageResource(userPermissions, resource) ||
    hasPermission(userPermissions, 'settings:read');
  const csView =
    canView(userPermissions, 'help-center') ||
    canView(userPermissions, 'banners') ||
    canView(userPermissions, 'general-inquiry') ||
    canViewAnyPage(CS_PAGE_RESOURCES);

  /*
   * ── 프론트 페이지 편집기가 부르는 '남의 도메인' API 권한 ─────────────────
   *
   * 프론트 페이지 설정 화면의 단추는 오랫동안 `canEditFrontendPages` 하나로만 잠겨
   * 있었는데, 그 화면이 실제로 치는 API 는 상품·배너·셀러 쪽이라 요구 권한이 다르다.
   * 그래서 단추는 눌리는데 저장이 403 으로 죽는 자리가 생겼다. 아래 플래그들은
   * **백엔드 라우트가 받는 코드 목록을 그대로 옮겨 적은 것**이다(추가만 한다 —
   * 기존 플래그의 뜻은 다른 화면들이 쓰고 있으므로 건드리지 않는다).
   *
   * 주의: 여기서는 일부러 `hasGranularPermission` 을 쓰지 않고 코드를 문자 그대로
   * 보는 자리가 있다. `utils/permissions.ts` 의 클라이언트 별칭표는 live-products:*
   * 의 레거시로 live-sellers:read/write 를 적어 두었고 판정이 **양방향**이라, 라이브
   * 셀러 담당자를 '상품 권한 있음'으로 읽는다. 백엔드 LEGACY_PERMISSION_ALIASES 에는
   * 그 별칭이 없어 실제로는 403 이다 — 그 갈래만 빼려면 문자 그대로 볼 수밖에 없다.
   */
  // GET /admin/own-mall/products[/:id] — requireAnyPermission(['company-mall:view','live-products:view'])
  // (백엔드 별칭 전개: company-mall:read | categories:read | products:read 도 통과한다)
  const ownMallProductView =
    hasGranularPermission(userPermissions, 'company-mall:view') ||
    userPermissions.includes('categories:read') ||
    userPermissions.includes('live-products:view');
  // POST /admin/own-mall/products/upload-image, PATCH·DELETE .../:id/landing-image
  // — requireAnyPermission(['company-mall:edit','live-products:edit'])
  const ownMallProductEdit =
    hasGranularPermission(userPermissions, 'company-mall:edit') ||
    userPermissions.includes('categories:write') ||
    userPermissions.includes('live-products:edit');
  // PUT /admin/own-mall/categories/:id — requirePermission('company-mall:write').
  // 역방향 별칭으로 company-mall:edit · live-products:edit · live-products:delete 도 통과하고,
  // categories:write 는 company-mall:edit 의 별칭일 뿐이라 이 라우트는 넘지 못한다.
  const ownMallCategoryEdit = [
    'company-mall:write',
    'company-mall:edit',
    'live-products:edit',
    'live-products:delete',
  ].some((code) => userPermissions.includes(code));
  // /admin/carousels — requirePermission('banner-event:read' | 'banner-event:write').
  // 아래 canViewBanners/canEditBanners(=CS 배너 화면 게이트)는 레거시 banners:*·carousels:*
  // 까지 받아 더 넓다. 그 코드만 가진 계정은 캐러셀 API 를 통과하지 못하므로(라우트가 받는
  // 것은 banner-event:read/write 와 그 정규화 코드뿐) 캐러셀 편집기는 이 좁은 쪽을 본다.
  const bannerEventView =
    userPermissions.includes('banner-event:read') || userPermissions.includes('banner-event:view');
  const bannerEventEdit =
    userPermissions.includes('banner-event:write') || userPermissions.includes('banner-event:edit');
  // 라이브 세션 편집 — 백엔드 FRONTEND_PAGES_EDIT_PERMISSIONS 가 live-sellers:write 를
  // 그대로 받는다. 프론트 페이지 권한이 없는 셀러 담당자도 편집할 수 있어야 한다.
  const liveSellersEdit =
    userPermissions.includes('live-sellers:edit') || userPermissions.includes('live-sellers:write');

  return {
    // 대시보드
    canViewDashboard: true,

    // Operation Data — gated like any other page (it shows sensitive operational metrics).
    // The backend issues the granular `operation-data:view` code for this row.
    canViewOperationData: isSuperAdmin || operationDataView,

    // 운영관리 menu group (parent route) — visible if the admin can view ANY page in the
    // group (operation data, product pages, or operation settings). The individual child
    // pages keep their own stricter access checks; this only governs whether the group
    // header shows, so an operation-data-only admin still sees the section they can enter.
    canViewOperationSection:
      isSuperAdmin ||
      operationDataView ||
      productsView ||
      productsEdit ||
      operationSettingsView ||
      shareRebateSettingsView,

    // 운영관리 — per-page menu & route gates (parent group uses canViewOperationSection)
    canViewCategoryManagement: isSuperAdmin || viewOperationPage('category-management'),
    canViewCompanyMall: isSuperAdmin || viewOperationPage('company-mall'),
    canViewLiveProducts: isSuperAdmin || viewOperationPage('live-products'),
    canViewLiveSellers: isSuperAdmin || viewOperationPage('live-sellers'),
    canViewSellerTeams: isSuperAdmin || viewOperationPage('seller-teams'),
    // 로그인 응답은 저장된 권한 코드를 그대로 싣고 오고(별칭 확장은 서버 라우트 검사에서만 일어난다)
    // 백엔드 라우트는 frontend-pages:* 와 live-sellers:* 를 함께 받고 구 live-page-settings:* 도
    // 별칭으로 통과시키므로, 아직 권한을 재저장하지 않은 담당자가 메뉴를 잃지 않도록
    // 화면에서도 같은 코드 목록을 받는다.
    // 레거시 두 줄은 viewOperationPage 가 아니라 canViewPageResource 를 직접 쓴다 —
    // viewOperationPage('live-sellers') 는 레거시 products:read 폴백까지 열어 과잉 노출이 된다.
    canViewFrontendPages:
      isSuperAdmin ||
      viewOperationPage('frontend-pages') ||
      canViewPageResource(userPermissions, 'live-page-settings') ||
      canViewPageResource(userPermissions, 'live-sellers'),
    // M2 의 편집 게이트에서 쓴다(현재 소비처 없음).
    // 백엔드 수용 목록(FRONTEND_PAGES_EDIT_PERMISSIONS + live-page-settings 별칭)과 같은 범위여야
    // 실제로 편집 가능한 담당자가 화면에서 버튼을 잃지 않는다.
    canEditFrontendPages:
      isSuperAdmin ||
      hasGranularPermission(userPermissions, 'frontend-pages:edit') ||
      hasGranularPermission(userPermissions, 'live-page-settings:edit') ||
      hasGranularPermission(userPermissions, 'live-sellers:edit') ||
      hasGranularPermission(userPermissions, 'live-sellers:write'),
    // 라이브 세션(편성) 편집 권한. `canEditFrontendPages` 와 **합집합**으로 써야 백엔드와
    // 맞는다 — 세션 라우트는 frontend-pages:edit 과 live-sellers:write 를 함께 받는데,
    // 화면이 프론트 페이지 권한만 보면 셀러 담당자가 읽기전용으로 잠긴다.
    canEditLiveSellers: isSuperAdmin || liveSellersEdit,
    // 프론트 페이지 편집기가 부르는 상품 API 권한(카드 피커·이름 보강·랜딩 이미지·이미지 업로드).
    // Some backend environments issue `access: PLATFORM_ADMIN` instead of `role: superadmin`.
    // Treat both as full access for "own-mall product" edit APIs (e.g. landing-image).
    canViewOwnMallProduct: isPlatformAdmin || ownMallProductView,
    canEditOwnMallProduct: isPlatformAdmin || ownMallProductEdit,
    // 해외직구 칩 편집기가 칩의 카테고리 그림을 카테고리 관리로 저장한다
    // — PUT /admin/categories/:id 는 requirePermission('category-management:write').
    canEditCategoryManagement:
      isSuperAdmin || hasGranularPermission(userPermissions, 'category-management:edit'),
    // 자사몰 카테고리 칩 편집기가 칩의 카테고리 그림을 자사몰 카테고리로 바로 저장한다.
    canEditOwnMallCategory: isSuperAdmin || ownMallCategoryEdit,
    // 캐러셀(히어로 배너) API 권한. 위 canViewBanners/canEditBanners 보다 좁다(주석 참고).
    canViewBannerEvent: isSuperAdmin || bannerEventView,
    canEditBannerEvent: isSuperAdmin || bannerEventEdit,
    canViewRebateSettings: isSuperAdmin || viewOperationSettingsPage('rebate-settings'),
    // 공유 리베이트 설정 — 백엔드 GET/PUT /admin/share-rebates/settings 와 같은 코드.
    canViewShareRebateSettings: isSuperAdmin || shareRebateSettingsView,
    canEditShareRebateSettings:
      isSuperAdmin || hasGranularPermission(userPermissions, 'share-rebate-settings:edit'),
    canViewSearchManagement: isSuperAdmin || viewOperationSettingsPage('search-management'),

    // 상품 - Superadmin has full access, others check permissions
    canManageProducts: isSuperAdmin || productsView || productsEdit,
    canViewProducts: isSuperAdmin || productsView,
    canCreateProduct:
      isSuperAdmin ||
      hasPermission(userPermissions, 'products:write') ||
      canEditAnyPage(['company-mall', 'live-products', 'live-sellers']),
    canImportTaobao: isSuperAdmin || hasPermission(userPermissions, 'products:sync'),
    canManageCategories:
      isSuperAdmin ||
      canView(userPermissions, 'categories') ||
      canViewPageResource(userPermissions, 'category-management'),

    // 주문 - granular order-status permissions + legacy fallback
    canManageOrders: isSuperAdmin || hasOrderView || hasOrderEdit,
    canViewOrders: isSuperAdmin || hasOrderView,
    canEditOrders: isSuperAdmin || hasOrderEdit,
    // 주문보기 › 운송장번호 수정 — its own permission, with no fallback: the backend route takes
    // order-waybill-number:edit only, and an order-status edit grant must not imply it.
    canEditOrderWaybillNumber:
      isSuperAdmin || hasGranularPermission(userPermissions, 'order-waybill-number:edit'),
    canProcessOrderInquiry:
      isSuperAdmin ||
      hasGranularPermission(userPermissions, 'order-questions:view') ||
      hasGranularPermission(userPermissions, 'order-questions:process') ||
      hasPermission(userPermissions, 'orders:inquiry'),

    // 물류 - Superadmin has full access, others check permissions
    canManageLogistics: isSuperAdmin || logisticsView || logisticsEdit,
    canViewLogistics: isSuperAdmin || logisticsView,
    canEditLogistics: isSuperAdmin || logisticsEdit,

    // 창고 - Superadmin has full access, others check permissions
    // The /storehouse menu group also hosts the logistics pages, so the group view flag
    // must include logistics view; otherwise an admin with only logistics permissions
    // would have the whole 창고관리 group hidden.
    canManageStorehouse: isSuperAdmin || storehouseGroupView,
    canViewStorehouse: isSuperAdmin || storehouseGroupView,
    canEditStorehouse:
      isSuperAdmin ||
      canEdit(userPermissions, 'warehouses') ||
      canEdit(userPermissions, 'racks') ||
      canEditAnyPage(STOREHOUSE_PAGE_RESOURCES),
    canViewRacks:
      isSuperAdmin || canView(userPermissions, 'racks') || canViewPageResource(userPermissions, 'rack-management'),
    canEditRacks:
      isSuperAdmin || canEdit(userPermissions, 'racks') || hasGranularPermission(userPermissions, 'rack-management:edit'),

    // 창고/물류 — per-page menu & route gates (parent group still uses canViewStorehouse)
    // 재고관리는 전용 권한(inventory-management:view)만으로 연다 — 창고관리(warehouses:read) 폴백을
    // 일부러 쓰지 않는다. viewStorehousePage 는 warehouses:read 를 함께 통과시켜, 재고 권한이 없는
    // 창고 담당자에게도 재고관리 메뉴·페이지가 열렸다. 백엔드 라우트는 inventory-management:view/edit
    // 전용이라 폴백이 없으므로(별칭 미등록), 화면도 같은 범위로 맞춘다.
    canViewOwnMallInventory: isSuperAdmin || canViewPageResource(userPermissions, 'inventory-management'),
    canEditOwnMallInventory:
      isSuperAdmin || hasGranularPermission(userPermissions, 'inventory-management:edit'),
    canViewInspectionList: isSuperAdmin || viewStorehousePage('inspection-list'),
    canViewWarehouseAddress: isSuperAdmin || viewStorehousePage('warehouse-address'),
    canViewWaybillInformations: isSuperAdmin || viewLogisticsPage('waybill-informations'),
    canViewWaybillBandwidth: isSuperAdmin || viewLogisticsPage('waybill-bandwidth'),
    canEditWaybillBandwidth:
      isSuperAdmin ||
      hasGranularPermission(userPermissions, 'waybill-bandwidth:edit') ||
      hasGranularPermission(userPermissions, 'waybill-bandwidth:write'),
    canViewTransportationMethods: isSuperAdmin || viewLogisticsPage('transportation-methods'),
    canEditTransportationMethods:
      isSuperAdmin ||
      hasGranularPermission(userPermissions, 'transportation-methods:edit') ||
      hasGranularPermission(userPermissions, 'transportation-methods:write'),
    canViewRegionalManagement: isSuperAdmin || viewLogisticsPage('regional-management'),
    canViewCollectOrders: isSuperAdmin || viewLogisticsPage('collect-orders'),
    canViewGoodsManagement: isSuperAdmin || viewLogisticsPage('goods-management'),
    canViewInternationalTrackingNumber:
      isSuperAdmin || viewLogisticsPage('international-tracking-number'),
    canViewHsProductCode: isSuperAdmin || viewLogisticsPage('hs-product-code'),

    // 회원 - Superadmin has full access, others check permissions
    // canManageMembers: view OR edit OR delete (users:read OR users:write OR users:delete)
    canManageMembers: isSuperAdmin || membersView,
    // canViewMembers: allow users with read, edit, OR delete - need to view page to edit/delete
    canViewMembers: isSuperAdmin || membersView,
    canEditMembers:
      isSuperAdmin ||
      hasPermission(userPermissions, 'users:write') ||
      canEditAnyPage(['general-members', 'member-documents']),
    canDeleteMembers: isSuperAdmin || hasPermission(userPermissions, 'users:delete'),
    // 회원 엑셀 다운 — granular matrix code, legacy users:write kept as fallback
    canExportMembers:
      isSuperAdmin ||
      hasGranularPermission(userPermissions, 'general-members:excel-download') ||
      hasPermission(userPermissions, 'users:write'),

    // 결제주문 엑셀 — its own permission, with no fallback: the file carries purchase cost per
    // line, which the ordinary order Excel does not, so an order-export grant must not imply it.
    canExportSettlementOrders:
      isSuperAdmin || hasGranularPermission(userPermissions, 'settlement-order-excel:excel-download'),

    // 정산 — each page requires its own granular permission (no orders:read fallback)
    canViewDepositApplicationHistory:
      isSuperAdmin || canViewPageResource(userPermissions, 'deposit-application-history'),
    canViewDepositUsageHistory:
      isSuperAdmin || canViewPageResource(userPermissions, 'deposit-usage-history'),
    canAccrueDeductDepositUsageHistory:
      isSuperAdmin || hasGranularPermission(userPermissions, 'deposit-usage-history:accrue-deduct'),
    canViewFinancialRecords:
      isSuperAdmin || canViewPageResource(userPermissions, 'financial-records'),
    canViewRefundRecords:
      isSuperAdmin ||
      canViewPageResource(userPermissions, 'refund-records') ||
      hasGranularPermission(userPermissions, 'user-orders:read'),
    canViewRebateRecords:
      isSuperAdmin || canViewPageResource(userPermissions, 'rebate-records'),
    canViewPointsHistory:
      isSuperAdmin || canViewPageResource(userPermissions, 'points-history'),
    canViewCouponUsageHistory:
      isSuperAdmin ||
      canViewPageResource(userPermissions, 'coupon-usage-history') ||
      canView(userPermissions, 'coupons'),
    canViewTaxSettlement:
      isSuperAdmin || canViewPageResource(userPermissions, 'tax-settlement'),
    // 공유 리베이트 모니터링 — 조회 / 행 조작·정산 실행·공유자 조작 / 엑셀.
    canViewShareRebates: isSuperAdmin || shareRebatesView,
    canManageShareRebates:
      isSuperAdmin || hasGranularPermission(userPermissions, 'share-rebates:confirm-cancel'),
    // 강사 승인·반려·해제·바로 지정·내부 메모 — 백엔드 /admin/share-rebates/instructors/* 와 같은 코드.
    canManageShareRebateInstructors:
      isSuperAdmin || hasGranularPermission(userPermissions, 'share-rebates:confirm-cancel'),
    canExportShareRebates:
      isSuperAdmin || hasGranularPermission(userPermissions, 'share-rebates:excel-download'),
    canViewOrderStatistics:
      isSuperAdmin ||
      hasGranularPermission(userPermissions, 'order-statistics:view') ||
      canViewPageResource(userPermissions, 'order-statistics'),
    canViewMemberStatistics:
      isSuperAdmin || canViewPageResource(userPermissions, 'member-statistics'),
    canViewActiveUsers:
      isSuperAdmin ||
      canViewPageResource(userPermissions, 'active-users') ||
      canViewPageResource(userPermissions, 'member-statistics'),
    // 사이트 통계(통계 › 방문·클릭 분석) — 조회 / 실시간 접속자 목록 내보내기.
    // 회원관리 그룹 헤더(MEMBER_PAGE_RESOURCES)에는 넣지 않는다: 이 권한만 가진 관리자에게
    // 회원관리 메뉴가 열리면 안 된다.
    canViewSiteAnalytics: isSuperAdmin || canViewPageResource(userPermissions, 'site-analytics'),
    canExportSiteAnalytics:
      isSuperAdmin || hasGranularPermission(userPermissions, 'site-analytics:excel-download'),
    canViewCouponPointSettings:
      isSuperAdmin || canViewPageResource(userPermissions, 'coupon-point-settings'),
    canViewPendingPayment:
      isSuperAdmin || canViewPageResource(userPermissions, 'pending-payment'),
    canViewDeposit:
      isSuperAdmin ||
      canViewPageResource(userPermissions, 'deposit-application-history') ||
      hasPermission(userPermissions, 'deposit:read'),
    canManageSettlement:
      isSuperAdmin ||
      canViewPageResource(userPermissions, 'deposit-application-history') ||
      canViewPageResource(userPermissions, 'deposit-usage-history') ||
      canViewPageResource(userPermissions, 'financial-records') ||
      canViewPageResource(userPermissions, 'pending-payment') ||
      canViewPageResource(userPermissions, 'refund-records') ||
      canViewPageResource(userPermissions, 'rebate-records') ||
      shareRebatesView,
    canViewSettlement:
      isSuperAdmin ||
      canViewPageResource(userPermissions, 'deposit-application-history') ||
      canViewPageResource(userPermissions, 'deposit-usage-history') ||
      canViewPageResource(userPermissions, 'financial-records') ||
      canViewPageResource(userPermissions, 'pending-payment') ||
      canViewPageResource(userPermissions, 'refund-records') ||
      canViewPageResource(userPermissions, 'rebate-records') ||
      shareRebatesView,
    canProcessSettlement:
      isSuperAdmin ||
      hasGranularPermission(userPermissions, 'pending-payment:confirm-cancel') ||
      hasGranularPermission(userPermissions, 'deposit-application-history:confirm-cancel'),

    // 고객센터 - Superadmin has full access, others check permissions
    canManageCS: isSuperAdmin || csView,
    canViewHelpCenter:
      isSuperAdmin || canView(userPermissions, 'help-center') || canViewPageResource(userPermissions, 'help-center'),
    canEditHelpCenter:
      isSuperAdmin || canEdit(userPermissions, 'help-center') || hasGranularPermission(userPermissions, 'help-center:edit'),
    canViewBanners:
      isSuperAdmin || canView(userPermissions, 'banners') || canViewPageResource(userPermissions, 'banner-event'),
    canEditBanners:
      isSuperAdmin || canEdit(userPermissions, 'banners') || hasGranularPermission(userPermissions, 'banner-event:edit'),
    canProcessGeneralInquiry:
      isSuperAdmin ||
      hasPermission(userPermissions, 'general:inquiry') ||
      canViewPageResource(userPermissions, 'inquiry') ||
      hasGranularPermission(userPermissions, 'inquiry:process'),

    // 시스템 - Superadmin has full access, others check permissions
    isSuperAdmin,
    canViewAdmins: isSuperAdmin || hasGranularPermission(userPermissions, 'administrators:view'),
    canEditAdmins: isSuperAdmin || hasGranularPermission(userPermissions, 'administrators:edit'),
    canDeleteAdmins: isSuperAdmin || hasPermission(userPermissions, 'admins:delete'),
    canManagePermissions:
      isSuperAdmin ||
      hasPermission(userPermissions, 'admins:write') ||
      hasGranularPermission(userPermissions, 'administrators:edit'), // permission editing lives on the administrators page
    canViewSettings: isSuperAdmin || settingsView,
    canEditSettings: isSuperAdmin || settingsEdit,
    canManageSettings: isSuperAdmin || settingsView || settingsEdit,

    // 설정 menu group — visible if the admin can view ANY settings page; each child keeps its own gate.
    canViewSettingsSection: isSuperAdmin || systemSettingsGroupView,

    // 설정 — per-page menu & route gates (parent group uses canViewSettingsSection)
    canViewExchangeTariffSetting: isSuperAdmin || viewSystemSettingsPage('exchange-tariff-setting'),
    canViewAddOnServices: isSuperAdmin || viewSystemSettingsPage('add-on-services'),
    canViewIslandMountainCost: isSuperAdmin || viewSystemSettingsPage('island-mountain-cost'),
    canViewWorkerLog: isSuperAdmin || viewSystemSettingsPage('worker-log'),
    canViewProductPriceIncrease: isSuperAdmin || viewSystemSettingsPage('product-price-increase'),
    canViewPurchaseConditionSetting: isSuperAdmin || viewSystemSettingsPage('purchase-condition-setting'),
    canViewServiceFeeSetting: isSuperAdmin || viewSystemSettingsPage('service-fee-setting'),

    // Additional Service Prices
    canViewAdditionalServicePrices:
      isSuperAdmin ||
      hasPermission(userPermissions, 'additional-service-price:read') ||
      canViewPageResource(userPermissions, 'additional-service-price'),
    canEditAdditionalServicePrices:
      isSuperAdmin ||
      hasPermission(userPermissions, 'additional-service-price:write') ||
      hasGranularPermission(userPermissions, 'additional-service-price:edit'),

    // Helper function to check route access - Superadmin has full access
    canAccessRouteByName: (routeName: string) => {
      return isSuperAdmin || canAccessRoute(userPermissions, routeName);
    },

    // Helper function to check permission - Superadmin has all permissions
    hasPermission: (permission: string) => {
      return isSuperAdmin || hasGranularPermission(userPermissions, permission);
    },
  };
};
